import { AiSettings } from "@components/settings/AiSettings";
import { BrandingSettings } from "@components/settings/BrandingSettings";
import { DataRetentionSettings } from "@components/settings/DataRetentionSettings";
import { DocumentTemplates } from "@components/settings/DocumentTemplates";
import { GoogleIntegrationSettings } from "@components/settings/GoogleIntegration";
import { ResidencySettings } from "@components/settings/ResidencySettings";
import { SuperuserAuditPanel } from "@components/settings/SuperuserAuditPanel";
import { CountryCodeSelect } from "@components/shared/CountryCodeSelect";
import { SearchLocationSelect } from "@components/shared/SearchLocationSelect";
import {
  CANCEL_INVITATION,
  CREATE_INVITATION,
  IMPORT_DEMO_DATA,
  REMOVE_MEMBER,
  RESEND_INVITATION,
  UPDATE_MEMBER_ROLE,
  UPDATE_ORGANISATION_SEARCH_LOCATION,
} from "@lib/graphql/mutations";
import { ORGANISATION_SETTINGS_QUERY } from "@lib/graphql/queries";
import type { Invitation, OrganisationMember } from "@lib/types";
import { Loader2, Mail, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

type SettingsTab =
  | "members"
  | "templates"
  | "branding"
  | "ai_settings"
  | "residency"
  | "data_retention"
  | "integrations"
  | "superuser_audit";

export function OrganisationSettings() {
  const [{ data, fetching }, reexecute] = useQuery({ query: ORGANISATION_SETTINGS_QUERY });
  const [, createInvitation] = useMutation(CREATE_INVITATION);
  const [, resendInvitation] = useMutation(RESEND_INVITATION);
  const [, cancelInvitation] = useMutation(CANCEL_INVITATION);
  const [, updateMemberRole] = useMutation(UPDATE_MEMBER_ROLE);
  const [, removeMember] = useMutation(REMOVE_MEMBER);
  const [, importDemoData] = useMutation(IMPORT_DEMO_DATA);
  const [, updateSearchLocation] = useMutation(UPDATE_ORGANISATION_SEARCH_LOCATION);
  const [savingSearchLocation, setSavingSearchLocation] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTab>("members");
  const [importingDemo, setImportingDemo] = useState(false);
  const [demoResult, setDemoResult] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [errors, setErrors] = useState<string[]>([]);
  const [memberErrors, setMemberErrors] = useState<string[]>([]);
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [confirmingCancelId, setConfirmingCancelId] = useState<string | null>(null);
  const [invitationActionErrors, setInvitationActionErrors] = useState<string[]>([]);

  const org = data?.currentOrganisation;
  const members: OrganisationMember[] = org?.members || [];
  const invitations: Invitation[] = org?.pendingInvitations || [];
  const currentUserRole: string | null = org?.currentUserRole || null;
  const isAdmin = currentUserRole === "admin";

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    const result = await createInvitation({ input: { email, role } });
    const mutationErrors = result.data?.createInvitation?.errors || [];
    if (mutationErrors.length === 0) {
      setShowForm(false);
      setEmail("");
      setRole("member");
      reexecute({ requestPolicy: "network-only" });
    } else {
      setErrors(mutationErrors);
    }
  }

  async function handleRoleChange(membershipId: string, newRole: string) {
    setMemberErrors([]);
    const result = await updateMemberRole({ input: { membershipId, role: newRole } });
    const mutationErrors = result.data?.updateMemberRole?.errors || [];
    if (mutationErrors.length === 0) {
      reexecute({ requestPolicy: "network-only" });
    } else {
      setMemberErrors(mutationErrors);
    }
  }

  async function handleRemove(membershipId: string) {
    setMemberErrors([]);
    const result = await removeMember({ input: { membershipId } });
    const mutationErrors = result.data?.removeMember?.errors || [];
    if (mutationErrors.length === 0) {
      setConfirmingRemove(null);
      reexecute({ requestPolicy: "network-only" });
    } else {
      setConfirmingRemove(null);
      setMemberErrors(mutationErrors);
    }
  }

  async function handleResend(id: string) {
    setInvitationActionErrors([]);
    setResendingId(id);
    const result = await resendInvitation({ input: { id } });
    setResendingId(null);
    const mutationErrors = result.data?.resendInvitation?.errors || [];
    if (mutationErrors.length === 0) {
      reexecute({ requestPolicy: "network-only" });
    } else {
      setInvitationActionErrors(mutationErrors);
    }
  }

  async function handleCancelInvitation(id: string) {
    setInvitationActionErrors([]);
    const result = await cancelInvitation({ input: { id } });
    const mutationErrors = result.data?.cancelInvitation?.errors || [];
    if (mutationErrors.length === 0) {
      setConfirmingCancelId(null);
      reexecute({ requestPolicy: "network-only" });
    } else {
      setConfirmingCancelId(null);
      setInvitationActionErrors(mutationErrors);
    }
  }

  if (fetching) {
    return <p className="text-sm text-text-muted">Loading...</p>;
  }

  return (
    <div>
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">
          Organisation Settings
        </h1>
        <p className="mt-1 text-sm text-text-muted">Manage settings for {org?.name}</p>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-border">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab("members")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "members"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Members
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "templates"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab("branding")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "branding"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Branding
          </button>
          <button
            onClick={() => setActiveTab("ai_settings")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "ai_settings"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            AI Settings
          </button>
          <button
            onClick={() => setActiveTab("residency")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "residency"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Data Residency
          </button>
          <button
            onClick={() => setActiveTab("data_retention")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "data_retention"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Data Retention
          </button>
          <button
            onClick={() => setActiveTab("integrations")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "integrations"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Integrations
          </button>
          <button
            onClick={() => setActiveTab("superuser_audit")}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "superuser_audit"
                ? "border-accent text-accent"
                : "border-transparent text-text-muted hover:text-text-primary hover:border-border"
            }`}
          >
            Superuser audit
          </button>
        </nav>
      </div>

      {activeTab === "branding" && <BrandingSettings />}

      {activeTab === "ai_settings" && <AiSettings />}

      {activeTab === "residency" && <ResidencySettings />}

      {activeTab === "data_retention" && <DataRetentionSettings />}

      {activeTab === "integrations" && <GoogleIntegrationSettings />}

      {activeTab === "superuser_audit" && (
        <SuperuserAuditPanel
          isAdmin={isAdmin}
          notifyAdminsOnSuperuserAction={org?.notifyAdminsOnSuperuserAction ?? false}
          onSettingChanged={() => reexecute({ requestPolicy: "network-only" })}
        />
      )}

      {activeTab === "templates" && (
        <div className="mt-6">
          <DocumentTemplates />
        </div>
      )}

      {activeTab === "members" && (
        <>
          {/* Members */}
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-text-primary">Members</h2>

            {memberErrors.length > 0 && (
              <div className="mt-3 rounded-md bg-red-50 dark:bg-red-900/20 p-3">
                {memberErrors.map((error) => (
                  <p key={error} className="text-sm text-red-700 dark:text-red-400">
                    {error}
                  </p>
                ))}
              </div>
            )}

            <div className="mt-3 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-card-alt">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                      Joined
                    </th>
                    {isAdmin && (
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-card-hover">
                      <td className="px-4 py-3 text-sm font-medium text-text-primary">
                        <span className="inline-flex items-center gap-2">
                          {member.user.fullName}
                          {member.user.superuser && (
                            <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                              Rarion staff
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">{member.user.email}</td>
                      <td className="px-4 py-3">
                        {isAdmin ? (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            className="rounded-md border border-input-border bg-card px-2 py-1 text-xs font-medium text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                          >
                            <option value="member">member</option>
                            <option value="admin">admin</option>
                          </select>
                        ) : (
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              member.role === "admin"
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            }`}
                          >
                            {member.role}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {new Date(member.createdAt).toLocaleDateString()}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          {confirmingRemove === member.id ? (
                            <span className="flex items-center gap-2">
                              <button
                                onClick={() => handleRemove(member.id)}
                                className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmingRemove(null)}
                                className="rounded-md border border-input-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setConfirmingRemove(member.id)}
                              className="rounded-md p-1 text-text-faint hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                              title="Remove member"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pending Invitations */}
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text-primary">Pending Invitations</h2>
              {isAdmin && (
                <button
                  onClick={() => setShowForm(!showForm)}
                  className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
                >
                  <Plus size={16} /> Invite Member
                </button>
              )}
            </div>

            {showForm && (
              <form
                onSubmit={handleInvite}
                className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
              >
                {errors.length > 0 && (
                  <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3">
                    {errors.map((error) => (
                      <p key={error} className="text-sm text-red-700 dark:text-red-400">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-text-secondary">Email *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="colleague@example.com"
                      className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary">Role</label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    type="submit"
                    className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
                  >
                    Send Invitation
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setErrors([]);
                    }}
                    className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {invitationActionErrors.length > 0 && (
              <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                {invitationActionErrors.map((err) => (
                  <p key={err}>{err}</p>
                ))}
              </div>
            )}

            {invitations.length === 0 ? (
              <div className="mt-3 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
                <Mail size={24} className="mx-auto text-text-faint" />
                <p className="mt-2 text-sm text-text-muted">No pending invitations.</p>
              </div>
            ) : (
              <div className="mt-3 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-card-alt">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                        Invited By
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                        Expires
                      </th>
                      {isAdmin && (
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invitations.map((invitation) => (
                      <tr key={invitation.id} className="hover:bg-card-hover">
                        <td className="px-4 py-3 text-sm font-medium text-text-primary">
                          {invitation.email}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              invitation.role === "admin"
                                ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                                : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            }`}
                          >
                            {invitation.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-secondary">
                          {invitation.invitedBy.fullName}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-secondary">
                          {new Date(invitation.expiresAt).toLocaleDateString()}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-2">
                              <button
                                onClick={() => handleResend(invitation.id)}
                                disabled={resendingId === invitation.id}
                                className="rounded-md p-1 text-text-faint hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50 dark:hover:bg-blue-900/20"
                                title="Resend invitation"
                              >
                                {resendingId === invitation.id ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <RefreshCw size={16} />
                                )}
                              </button>
                              {confirmingCancelId === invitation.id ? (
                                <span className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleCancelInvitation(invitation.id)}
                                    className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setConfirmingCancelId(null)}
                                    className="rounded-md border border-input-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
                                  >
                                    Cancel
                                  </button>
                                </span>
                              ) : (
                                <button
                                  onClick={() => setConfirmingCancelId(invitation.id)}
                                  className="rounded-md p-1 text-text-faint hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                                  title="Cancel invitation"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Demo Data */}
          {isAdmin && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-text-primary">Demo Data</h2>
              <p className="mt-1 text-sm text-text-muted">
                Import a demo campaign with example candidates, assessments, and pipeline data to
                explore the platform.
              </p>
              <div className="mt-3">
                {demoResult && (
                  <p className="mb-3 text-sm text-green-600 dark:text-green-400">{demoResult}</p>
                )}
                <button
                  onClick={async () => {
                    if (!confirm("Import demo campaign data? This will create example records."))
                      return;
                    setImportingDemo(true);
                    setDemoResult(null);
                    const result = await importDemoData({ input: {} });
                    const errs = result.data?.importDemoData?.errors || [];
                    if (errs.length === 0) {
                      const title = result.data?.importDemoData?.campaign?.title;
                      setDemoResult(`Demo campaign "${title}" created successfully.`);
                    } else {
                      setDemoResult(`Error: ${errs.join(", ")}`);
                    }
                    setImportingDemo(false);
                  }}
                  disabled={importingDemo}
                  className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
                >
                  {importingDemo ? "Importing..." : "Import Demo Data"}
                </button>
              </div>
            </div>
          )}

          {/* Default Search Location */}
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-text-primary">Default Search Location</h2>
            <p className="mt-1 text-sm text-text-muted">
              Countries to geo-target X-Ray searches from by default. Can be overridden per-client
              or per-role on the campaign brief.
            </p>
            <div className="mt-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
              <CountryCodeSelect
                value={org?.searchCountryCodes ?? []}
                onChange={async (codes) => {
                  setSavingSearchLocation(true);
                  await updateSearchLocation({
                    input: { searchCountryCodes: codes },
                  });
                  reexecute({ requestPolicy: "network-only" });
                  setSavingSearchLocation(false);
                }}
              />
              <div className="mt-3">
                <SearchLocationSelect
                  value={org?.searchLocations ?? []}
                  onChange={async (locations) => {
                    setSavingSearchLocation(true);
                    await updateSearchLocation({
                      input: { searchLocations: locations },
                    });
                    reexecute({ requestPolicy: "network-only" });
                    setSavingSearchLocation(false);
                  }}
                  label="Narrower Locations"
                  hint="Cities or regions within those countries, if searches aren't country-wide."
                />
              </div>
              {savingSearchLocation && <p className="mt-1 text-xs text-text-muted">Saving...</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
